import React, { useContext } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import './App.css';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Importar los nuevos componentes
import BackendWakeupLoader from './components/BackendWakeupLoader';
import { useBackendStatus } from './hooks/useBackendStatus';

// Layouts
import PublicLayout from './layouts/PublicLayout';
import PrivateLayout from './layouts/PrivateLayout';
import AdminLayout from './layouts/AdminLayout';

// Componentes de Página/Sección
import Login from './components/Login';
import Home from './components/Home';
import ProductStore from './components/ProductStore';
import About from './components/About';
import Treatments from './components/Treatments';
import Blog from './components/Blog';
import Members from './components/Members';
import Contact from './components/Contact';

// Componentes de gestión del Admin
import ManageTreatments from './components/ManageTreatments';
import ManageBlogs from './components/ManageBlogs';
import ManageEmployees from './components/ManageEmployees';
import ManageProducts from './components/ManageProducts';
import ManageServicesPacks from './components/ManageServicesPacks';
import ManageClientServicesPacks from './components/ManageClientServicesPacks';
import ServicesPackSessionForm from './components/ServicesPackSessionForm';
import ManageClients from './components/ManageClients';
import ManageAppointments from './components/ManageAppointments';
import ManageEmployeeAvailabilities from './components/ManageEmployeeAvailabilities';
import MetricsDashboard from './components/MetricsDashboard';
import SendCustomMessage from './components/SendCustomMessage';
import EmployeePayroll from './components/EmployeePayroll';
import ChangePassword from './components/ChangePassword';
import Dashboard from './components/Dashboard';

// Contexto
import { AuthContext, AuthProvider, UserRole } from './auth/authContext';
import { PERMISSIONS } from './auth/permissions';
import ManageUsers from './components/ManageUsers';

interface GuardProps {
  children: React.ReactElement;
  anyOf?: string[];
  adminOnly?: boolean;
}

// Guard de ruta: verifica los permisos del usuario antes de renderizar la sección
const Protected: React.FC<GuardProps> = ({ children, anyOf, adminOnly }) => {
  const { hasRole, hasAnyPermission } = useContext(AuthContext);

  const allowed = adminOnly
    ? hasRole(UserRole.ADMIN)
    : !anyOf || anyOf.length === 0 || hasAnyPermission(...anyOf);

  if (!allowed) {
    return (
      <div className="no-permission-message">
        No tienes permisos para ver esta sección.
      </div>
    );
  }

  return children;
};

function App() {
  const { isBackendReady, isWakingUp } = useBackendStatus();

  return (
    <AuthProvider>

      {/* Mostrar loader mientras el backend se está despertando */}
      <BackendWakeupLoader 
        isBackendLoading={isWakingUp || isBackendReady === false}
        estimatedTime={45}
      />
      
      <Router>
        <ToastContainer
          position="top-right"
          autoClose={5000} // El toast se cierra después de 5 segundos
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        <Routes>
          {/* Rutas Públicas (anidadas bajo PublicLayout) */}
          <Route path="/" element={<PublicLayout />}>
            <Route index element={<Home />} />
            <Route path="store" element={<ProductStore />} />
            <Route path="about" element={<About />} />
            <Route path="treatments" element={<Treatments />} />
            <Route path="blog" element={<Blog />} />
            <Route path="members" element={<Members />} />
            <Route path="contact" element={<Contact />} />
          </Route>

          {/* Ruta de Login */}
          <Route path="/login" element={<Login />} />

          {/* Rutas Privadas / Admin */}
          <Route element={<PrivateLayout />}>
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Dashboard />} />
              <Route
                path="treatments"
                element={
                  <Protected anyOf={[PERMISSIONS.TREATMENTS_MANAGE]}>
                    <ManageTreatments />
                  </Protected>
                }
              />
              <Route
                path="blogs"
                element={
                  <Protected anyOf={[PERMISSIONS.BLOGS_MANAGE]}>
                    <ManageBlogs />
                  </Protected>
                }
              />
              <Route
                path="employees"
                element={
                  <Protected anyOf={[PERMISSIONS.EMPLOYEES_MANAGE]}>
                    <ManageEmployees />
                  </Protected>
                }
              />
              <Route
                path="products"
                element={
                  <Protected
                    anyOf={[
                      PERMISSIONS.PRODUCTS_MANAGE,
                      PERMISSIONS.PRODUCT_SALES_CREATE,
                      PERMISSIONS.PRODUCT_SALES_VIEW,
                    ]}
                  >
                    <ManageProducts />
                  </Protected>
                }
              />
              <Route
                path="services-packs"
                element={
                  <Protected anyOf={[PERMISSIONS.SERVICES_PACKS_MANAGE]}>
                    <ManageServicesPacks />
                  </Protected>
                }
              />
              <Route
                path="client-packs"
                element={
                  <Protected anyOf={[PERMISSIONS.CLIENT_PACKS_MANAGE]}>
                    <ManageClientServicesPacks />
                  </Protected>
                }
              />
              <Route
                path="/admin/register-pack-session"
                element={
                  <Protected anyOf={[PERMISSIONS.CLIENT_PACKS_MANAGE]}>
                    <ServicesPackSessionForm
                      onSuccess={() => {
                        // Lógica después de éxito
                        alert('Sesión registrada exitosamente!');
                      }}
                      onCancel={() => {
                        // Volver atrás
                        window.history.back();
                      }}
                    />
                  </Protected>
                }
              />
              <Route
                path="clients"
                element={
                  <Protected anyOf={[PERMISSIONS.CLIENTS_VIEW]}>
                    <ManageClients />
                  </Protected>
                }
              />
              <Route
                path="appointments"
                element={
                  <Protected anyOf={[PERMISSIONS.APPOINTMENTS_VIEW]}>
                    <ManageAppointments />
                  </Protected>
                }
              />
              <Route
                path="employee-availabilities"
                element={
                  <Protected
                    anyOf={[PERMISSIONS.EMPLOYEE_AVAILABILITIES_MANAGE]}
                  >
                    <ManageEmployeeAvailabilities />
                  </Protected>
                }
              />
              <Route
                path="metrics"
                element={
                  <Protected anyOf={[PERMISSIONS.METRICS_VIEW]}>
                    <MetricsDashboard />
                  </Protected>
                }
              />
              <Route
                path="send-message"
                element={
                  <Protected anyOf={[PERMISSIONS.WHATSAPP_SEND]}>
                    <SendCustomMessage />
                  </Protected>
                }
              />
              <Route
                path="payroll"
                element={
                  <Protected anyOf={[PERMISSIONS.PAYROLL_VIEW]}>
                    <EmployeePayroll />
                  </Protected>
                }
              />
              <Route
                path="users"
                element={
                  <Protected adminOnly>
                    <ManageUsers />
                  </Protected>
                }
              />
              <Route path="change-password" element={<ChangePassword />} />
            </Route>
          </Route>

          {/* Ruta Catch-all o Not Found (Opcional) */}
          <Route path="*" element={<h1>404 - Página no encontrada</h1>} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
